import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Power } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { clockApi, type ClockSettings } from "../../../api/clock";
import { Button, TextInput } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";

function Row({ k, cs, onSaved }: { k: string; cs: ClockSettings; onSaved: () => void }) {
  const initial = cs.values[k] == null ? "" : String(cs.values[k]);
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const spec = cs.specs[k];
  const save = async () => {
    setBusy(true);
    try {
      await clockApi.update(k, k === "payable_time_default" ? v : Number(v));
      toast.success(`${k} saved`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`clock-setting-${k}`} className="grid gap-2 border-b border-white/5 py-3 md:grid-cols-[260px_1fr_auto] md:items-center">
      <div><p className="font-mono text-xs text-white">{k}</p>{initial === "" && <p className="text-[11px] font-semibold uppercase text-signal">Not set</p>}</div>
      <div>
        {k === "payable_time_default" ? (
          <select value={v} onChange={(e) => setV(e.target.value)} data-testid={`clock-setting-${k}-input`} className="h-10 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white">
            <option value="">—</option>{cs.payable_options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <TextInput type="number" min={spec.min ?? undefined} max={spec.max ?? undefined} value={v} onChange={(e) => setV(e.target.value)} data-testid={`clock-setting-${k}-input`} />
        )}
        <p className="mt-1 text-xs text-zinc-500">{spec.description}{spec.min != null ? ` Range ${spec.min}–${spec.max}.` : ""} Approved: {String(spec.approved_value)}.</p>
      </div>
      <Button variant="secondary" className="h-9 px-4 text-[11px]" disabled={v === initial || v === ""} loading={busy} onClick={save} data-testid={`clock-setting-${k}-save`}>Save</Button>
    </div>
  );
}

export function TimeclockSettings() {
  const [reload, setReload] = useState(0);
  const loader = useCallback(() => clockApi.settings(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<ClockSettings>(loader);
  const bump = () => setReload((r) => r + 1);
  const on = data?.values.clock_enabled === true;
  const toggle = async () => {
    try {
      await clockApi.update("clock_enabled", !on);
      toast.success(`Clock turned ${on ? "OFF" : "ON"}`);
      bump();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <section data-testid="timeclock-settings" className="rounded-sm border border-white/10 bg-surface p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Timeclock</h2>
        <span data-testid="clock-enabled-state" className={`rounded-sm px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${on ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-zinc-400"}`}>{on ? "Clock ON" : "Clock OFF"}</span>
        <Button variant={on ? "secondary" : "primary"} className="h-9 px-4 text-[11px]" onClick={toggle} disabled={!data} data-testid="clock-enabled-toggle"><Power className="h-4 w-4" /> Turn {on ? "off" : "on"}</Button>
        <Link to="/admin/settings/twilio-guide" data-testid="twilio-guide-link" className="ml-auto inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-signal hover:text-white"><BookOpen className="h-4 w-4" /> Twilio Setup Guide</Link>
      </div>
      <p className="mb-2 text-sm text-zinc-400">While OFF, IN/OUT texts get the "not live yet" reply and no punches are recorded. Turning ON requires every setting below. Every change is audited.</p>
      <LoadState error={error} loading={!data && !error} testId="timeclock-settings-load" />
      {data && Object.keys(data.specs).map((k) => <Row key={`${k}-${String(data.values[k])}`} k={k} cs={data} onSaved={bump} />)}
    </section>
  );
}
